import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { StatusBadge } from '../ui/status-badge';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Card, CardContent } from '../ui/card';
import { Bell, Calendar, Users, CheckCircle, XCircle, MousePointerClick, Eye, Send } from 'lucide-react';

interface NotificationViewModalProps {
    notification: any;
    onClose: () => void;
}

export default function NotificationViewModal({ notification, onClose }: NotificationViewModalProps) {
    if (!notification) return null;

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'sent': return 'default';
            case 'sending': return 'secondary';
            case 'pending': return 'outline';
            case 'failed': return 'destructive';
            case 'cancelled': return 'destructive';
            default: return 'outline';
        }
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0">
                <DialogHeader className="p-6 pb-2">
                    <div className="flex justify-between items-start">
                        <div>
                            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                                Notification Details
                                <StatusBadge status={notification.status} label={notification.status.toUpperCase()} />
                            </DialogTitle>
                            {notification.createdAt && (
                                <p className="text-muted-foreground mt-1 text-sm">
                                    Created {formatDate(notification.createdAt)}
                                </p>
                            )}
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex-1 p-6 pt-2 overflow-y-auto">
                    <div className="space-y-6">
                        {/* Content Preview */}
                        <div className="bg-muted/30 p-4 rounded-lg border space-y-3">
                            <h4 className="font-semibold flex items-center gap-2">
                                <Bell className="h-4 w-4" /> Content Preview
                            </h4>
                            <div className="bg-background border rounded-md p-4 shadow-sm max-w-sm mx-auto">
                                {notification.image && (
                                    <img
                                        src={notification.image}
                                        alt="Notification"
                                        className="w-full h-32 object-cover rounded-md mb-3"
                                    />
                                )}
                                <h5 className="font-bold text-sm mb-1">{notification.title}</h5>
                                <p className="text-sm text-muted-foreground">{notification.body}</p>
                            </div>
                        </div>

                        {/* Statistics */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <Card>
                                <CardContent className="p-4 flex flex-col items-center text-center">
                                    <Users className="h-5 w-5 text-muted-foreground mb-2" />
                                    <span className="text-2xl font-bold">{notification.totalTargeted || 0}</span>
                                    <span className="text-xs text-muted-foreground">Targeted</span>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="p-4 flex flex-col items-center text-center">
                                    <Send className="h-5 w-5 text-blue-500 mb-2" />
                                    <span className="text-2xl font-bold">{notification.totalSent || 0}</span>
                                    <span className="text-xs text-muted-foreground">Sent</span>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="p-4 flex flex-col items-center text-center">
                                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                                    <span className="text-2xl font-bold">{notification.totalDelivered || 0}</span>
                                    <span className="text-xs text-muted-foreground">Delivered</span>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="p-4 flex flex-col items-center text-center">
                                    <XCircle className="h-5 w-5 text-red-500 mb-2" />
                                    <span className="text-2xl font-bold">{notification.totalFailed || 0}</span>
                                    <span className="text-xs text-muted-foreground">Failed</span>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Engagement Stats (if available) */}
                        {(notification.clickCount !== undefined || notification.openCount !== undefined) && (
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-blue-50 p-4 rounded-lg flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Eye className="h-4 w-4 text-blue-600" />
                                        <span className="text-sm font-medium">Opens</span>
                                    </div>
                                    <span className="text-xl font-bold text-blue-700">{notification.openCount || 0}</span>
                                </div>
                                <div className="bg-purple-50 p-4 rounded-lg flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <MousePointerClick className="h-4 w-4 text-purple-600" />
                                        <span className="text-sm font-medium">Clicks</span>
                                    </div>
                                    <span className="text-xl font-bold text-purple-700">{notification.clickCount || 0}</span>
                                </div>
                            </div>
                        )}

                        <Separator />

                        {/* Details Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                            <div className="space-y-3">
                                <h4 className="font-semibold flex items-center gap-2">
                                    <Users className="h-4 w-4" /> Targeting Details
                                </h4>
                                <div className="grid grid-cols-2 gap-2">
                                    <Label className="text-muted-foreground">Target Type</Label>
                                    <p className="font-medium capitalize">{notification.targetType}</p>

                                    {notification.targetCategory && (
                                        <>
                                            <Label className="text-muted-foreground">Category</Label>
                                            <p className="font-medium">{notification.targetCategory}</p>
                                        </>
                                    )}

                                    {notification.locationFilter && (
                                        <>
                                            <Label className="text-muted-foreground">Location</Label>
                                            <p className="font-medium">
                                                {notification.locationFilter.cities?.length ? `${notification.locationFilter.cities.length} Cities` :
                                                    notification.locationFilter.states?.length ? `${notification.locationFilter.states.length} States` : 'Specific Locations'}
                                            </p>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-3">
                                <h4 className="font-semibold flex items-center gap-2">
                                    <Calendar className="h-4 w-4" /> Timeline
                                </h4>
                                <div className="grid grid-cols-2 gap-2">
                                    <Label className="text-muted-foreground">Created At</Label>
                                    <p className="font-medium">{formatDate(notification.createdAt)}</p>

                                    <Label className="text-muted-foreground">Sent At</Label>
                                    <p className="font-medium">{formatDate(notification.sentAt)}</p>

                                    {notification.completedAt && (
                                        <>
                                            <Label className="text-muted-foreground">Completed At</Label>
                                            <p className="font-medium">{formatDate(notification.completedAt)}</p>
                                        </>
                                    )}

                                    <Label className="text-muted-foreground">Created By</Label>
                                    <p className="font-medium">{notification.createdBy?.name || 'System'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-4 border-t flex justify-end">
                    <Button onClick={onClose}>Close</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
